import {
  cloneElement,
  Fragment,
  type Key,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Check, ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { inputStyle } from './input';

export type MultiSelectProps<V> = {
  id?: string;
  value?: V[];
  placeholder: string;
  options: V[];

  onChange: (value: V[]) => void;
  renderOption?: (option: V) => ReactNode;
  renderValue?: (value: V[]) => ReactNode;
  optionKey?: (option: V) => Key;

  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  resize?: boolean;
  theme?: 'looker';

  className?: string;
  rightIcon?: ReactElement<{ className?: string }>;
};

export function MultiSelect<V>({
  id,
  value = [],
  placeholder,
  options,

  onChange,
  renderOption,
  renderValue,
  optionKey = (o: V) => String(o),

  disabled = false,
  loading = false,
  error = false,
  theme,

  className,
  rightIcon,
}: MultiSelectProps<V>) {
  const style = selectStyle({
    loading,
    error,
    disabled,
    withRight: true,
  });

  return (
    <Listbox
      value={value}
      by={(a: V, b: V) => optionKey(a) === optionKey(b)}
      disabled={disabled || loading}
      onChange={onChange}
      multiple
    >
      {({ open }) => (
        <div className={style.container()}>
          <Listbox.Button id={id} className={style.input({ className })}>
            <MultiSelectValue<V>
              options={options}
              placeholder={placeholder}
              renderValue={renderValue}
              value={value}
            />
            <div className={style.inputRight()}>
              {rightIcon
                ? cloneElement(rightIcon, {
                    className: style.inputIcon({
                      className: rightIcon.props.className,
                    }),
                  })
                : null}
              {!rightIcon && theme !== 'looker' ? (
                <ChevronsUpDown
                  className={style.inputIcon({
                    className: 'pointer-events-auto',
                  })}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <div className={style.inputLoader()}>
              <LoaderCircle
                className={style.inputLoaderIcon()}
                aria-hidden="true"
              />
            </div>
          </Listbox.Button>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className={style.options()}>
              {options.map((option) => (
                <Listbox.Option
                  key={optionKey(option)}
                  className={style.option()}
                  value={option}
                >
                  <span className={style.optionValue()}>
                    {renderOption ? renderOption(option) : String(option)}
                  </span>
                  <span className={style.optionSelected()}>
                    <Check
                      className={style.optionSelectedIcon()}
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}

export const selectStyle = tv({
  extend: inputStyle,
  slots: {
    input: 'max-h-10 truncate',
    options: `absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none`,
    option: `relative flex cursor-default select-none flex-row items-center gap-2 py-2 pl-3 pr-9 text-neutral-700 ui-active:bg-primary-50 ui-active:text-primary-800`,
    optionValue: `block truncate font-normal ui-selected:font-semibold`,
    optionSelected: `absolute inset-y-0 right-0 hidden items-center pr-4 text-primary-600 ui-selected:flex ui-active:text-white`,
    optionSelectedIcon: `size-4`,
  },
  variants: {
    theme: {
      looker: {
        input: `
          rounded
          py-[6px]
          pl-4
          text-[#3a4245]
          ring-[#c3c3c3]
          hover:bg-[#f3f3f3]
          hover:ring-primary-400
          focus:ring-primary-400
        `,
        inputIcon: `
          rounded-full
          hover:bg-[#e7e7e7]/50
      `,
        placeholder: 'text-[#656b6d]',
      },
    },
    value: {
      true: {},
    },
  },
  compoundVariants: [
    {
      theme: 'looker',
      value: true,
      className: {
        input: `
          bg-primary-100
          hover:bg-primary-100
        `,
      },
    },
  ],
});

type MultiSelectValueProps<V> = {} & Pick<
  MultiSelectProps<V>,
  'options' | 'placeholder' | 'renderValue' | 'value'
>;

function MultiSelectValue<V>({
  options,
  placeholder,
  renderValue,
  value = [],
}: MultiSelectValueProps<V>) {
  if (value.length === 0) {
    return <span className="text-gray-400">{placeholder}</span>;
  }

  if (renderValue) {
    return <>{renderValue(value)}</>;
  }

  return (
    <span>
      {Object.values(options)
        .filter((o) => value.includes(o))
        .join(', ')}
    </span>
  );
}
